import { privateHttp } from "../../../config/axios";
import { API_ENDPOINTS } from "../../../config/endpoints";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { PetDataType } from "../../../types/common";

// 예약 목록 조회 요청 타입
// 서버(BusinessReserveController#findReservationList)는 상태/유형만 받는다.
// 날짜 파라미터는 서버에 존재하지 않으므로 전달하지 않는다(= 항상 전체 조회).
interface BusinessReserveListRequest {
  cdReservStatus?: string;
  cdReservType?: string;
}

// 예약 목록 응답 타입
interface BusinessReserveListResponse {
  statusCode: number;
  data: Array<{
    petSeq: number;
    reservationSeq: number;
    reservDetailSeq: number;
    petName: string;
    thumbnailUrl: string | null;
    petWeight: string;
    reservation: string;
    cdReservStatus: string;
    date?: string;
    ticketName?: string | null;
    cdReservType?: string;
    startDate?: string | null;
    endDate?: string | null;
    startTime?: string | null;
    endTime?: string | null;
  }>;
}

// 예약 상세 조회 요청 타입
interface BusinessReserveDetailRequest {
  reservationSeq: number;
}

// 예약 상세 응답 타입
interface PetInfoData {
  thumbnailUrl: string | null;
  petName: string;
  petWeight: string;
  petBreed: string;
  petAge: string;
  neuter: boolean;
}

interface AdditionalData {
  additionalName: string;
  active: boolean;
}

interface PersonalityData {
  personalityName: string;
  active: boolean;
}

interface TicketInfoData {
  purchaseTicketSeq: number;
  productTicketSeq: number;
  userSeq: number;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  qty: number;
  badge: string[];
  companyName: string;
  address: string;
  companySeq: number;
  purchaseDate: string;
  expiredDate: string;
  cdProductType: string;
}

interface BusinessReserveDetailResponse {
  statusCode: number;
  data: {
    reservationSeq: number;
    reservation: string;
    cdReservStatus?: string;
    date: string;
    paymentAmount: string;
    allergy: string;
    memo: string | null;
    everyWeek: boolean;
    props: Array<{
      type: "petInfo" | "additionals" | "personality" | "ticketInfo";
      datas: PetInfoData[] | AdditionalData[] | PersonalityData[] | TicketInfoData[];
    }>;
  };
}

// 예약 상태 업데이트 요청 타입
interface BusinessReserveUpdateRequest {
  reservationSeq: number;
  cdReservStatus: string;
}

// 예약 상태 업데이트 응답 타입
interface BusinessReserveUpdateResponse {
  statusCode: number;
  message: string;
}

// 예약 목록 조회 API
// 예약 정원 체크(비즈, 유치원) — 우리 매장 기준. 예약 진행 전 호출.
export interface BizReservationCapacityResult {
  limitEnabled: boolean;
  capacity: number | null;
  dates: { date: string; count: number; full: boolean }[];
  fullDates: string[];
}
export const checkBusinessReservationCapacity = async (
  dates: string[]
): Promise<BizReservationCapacityResult> => {
  const { data } = await privateHttp.get<{ data: BizReservationCapacityResult }>(
    API_ENDPOINTS.BUSINESS_RESERVE.CAPACITY_CHECK,
    { params: { dates: dates.join(",") } }
  );
  return (
    data?.data ?? { limitEnabled: false, capacity: null, dates: [], fullDates: [] }
  );
};

export const getBusinessReserveList = async (
  request: BusinessReserveListRequest
): Promise<BusinessReserveListResponse> => {
  const { data } = await privateHttp.get<BusinessReserveListResponse>(
    API_ENDPOINTS.BUSINESS_RESERVE.LIST,
    { params: request }
  );
  return data;
};

// 예약 상세 조회 API
export const getBusinessReserveDetail = async (
  request: BusinessReserveDetailRequest
): Promise<BusinessReserveDetailResponse> => {
  const { data } = await privateHttp.get<BusinessReserveDetailResponse>(
    API_ENDPOINTS.BUSINESS_RESERVE.DETAIL,
    { params: request }
  );
  return data;
};

// 수동(매뉴얼) 예약 - 이용권 목록 조회 (GET /business/manual/reserve/ticket-list, params: userSeq)
export interface BusinessManualReserveTicketItem {
  purchaseTicketSeq: number;
  productTicketSeq: number;
  userSeq: number;
  petSeq: number;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: string;
  remainCount: number;
  totalCount: number;
  badge: string[];
  companyName: string;
  address: string;
  companySeq: number;
  purchaseDate: string;
  usableDate: string;
  expiredDate: string;
  cdProductType: string;
  paymentType: string;
  refundable: boolean;
  // 데이케어 시간권: 구매 시점 최소 구매/이용 수량 스냅샷 (null이면 제한 없음)
  minPurchaseQty?: number | null;
  minUseQty?: number | null;
}

export interface BusinessManualReserveTicketListResponse {
  statusCode: number;
  message?: string;
  data?: BusinessManualReserveTicketItem[];
}

const getBusinessManualReserveTicketList = async (params: {
  userSeq: number;
}): Promise<BusinessManualReserveTicketListResponse> => {
  const { data } = await privateHttp.get<BusinessManualReserveTicketListResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.TICKET_LIST,
    { params }
  );
  return data;
};

// 이용권 보유 반려동물 목록 (수기 예약 시작용)
export interface TicketHolderPet {
  petSeq: number;
  petName: string;
  thumbnailUrl: string | null;
  userSeq: number;
  userName: string;
  ticketCount: number;
}

const getBusinessTicketHolderList = async (): Promise<TicketHolderPet[]> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.TICKET_HOLDER_LIST
  );
  return data.data ?? [];
};

export const useBusinessTicketHolderList = () => {
  return useQuery({
    queryKey: ["business", "manual", "reserve", "ticket-holder-list"],
    queryFn: getBusinessTicketHolderList,
  });
};

export const BUSINESS_MANUAL_RESERVE_QUERY_KEYS = {
  ticketList: ["business", "manual", "reserve", "ticket-list"] as const,
  petList: ["business", "manual", "reserve", "pet-list"] as const,
  reservableDate: ["business", "manual", "reserve", "reservable-date"] as const,
  reservableTime: ["business", "manual", "reserve", "reservable-time"] as const,
  contract: ["business", "manual", "reserve", "contract"] as const,
  calendar: ["business", "manual", "reserve", "calendar"] as const,
} as const;

export const useBusinessManualReserveTicketList = (params: { userSeq: number }) => {
  return useQuery({
    queryKey: [...BUSINESS_MANUAL_RESERVE_QUERY_KEYS.ticketList, params.userSeq],
    queryFn: () => getBusinessManualReserveTicketList(params),
    enabled: !!params.userSeq && params.userSeq > 0,
    select: (res) => res.data ?? [],
  });
};

// 수동(매뉴얼) 예약 - 반려동물 목록 조회 (GET /business/manual/reserve/pet-list, params: userSeq)
export interface BusinessManualReservePetListResponse {
  statusCode: number;
  message?: string;
  data?: PetDataType[];
}

const getBusinessManualReservePetList = async (params: {
  userSeq: number;
}): Promise<BusinessManualReservePetListResponse> => {
  const { data } = await privateHttp.get<BusinessManualReservePetListResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.PET_LIST,
    { params }
  );
  return data;
};

export const useBusinessManualReservePetList = (params: { userSeq: number }) => {
  return useQuery({
    queryKey: [...BUSINESS_MANUAL_RESERVE_QUERY_KEYS.petList, params.userSeq],
    queryFn: () => getBusinessManualReservePetList(params),
    enabled: !!params.userSeq && params.userSeq > 0,
    select: (res) => res.data ?? [],
  });
};

// 수동(매뉴얼) 예약 - 예약 가능 날짜 조회 (GET /business/manual/reserve/reservable/date, params: yearMonth, productType?, petSeq?)
// companySeq 는 서버가 토큰에서 꺼내므로 전달하지 않는다.
export interface BusinessManualReserveReservableDateResponse {
  statusCode: number;
  message?: string;
  data?: {
    calendar: unknown;
    reservedDates?: string[];
    holidays?: string[]; // 매장 임시 휴무일 (yyyyMMdd) — 호텔은 calendar에 미반영(체크인만 차단 정책)
  };
}

const getBusinessManualReserveReservableDate = async (params: {
  yearMonth: string;
  productType?: string;
  petSeq?: number;
}): Promise<BusinessManualReserveReservableDateResponse> => {
  const { data } = await privateHttp.get<BusinessManualReserveReservableDateResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.RESERVABLE_DATE,
    { params }
  );
  return data;
};

export const useBusinessManualReserveReservableDate = (
  yearMonth: string,
  enabled: boolean = true,
  productType?: string,
  petSeq?: number
) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_MANUAL_RESERVE_QUERY_KEYS.reservableDate,
      yearMonth,
      productType,
      petSeq,
    ],
    queryFn: () =>
      getBusinessManualReserveReservableDate({
        yearMonth,
        productType,
        petSeq,
      }),
    enabled: enabled && !!yearMonth,
    select: (res) => res.data ?? null,
  });
};

// 수동(매뉴얼) 예약 - 예약 가능 시간 조회 (GET /business/manual/reserve/reservable/time, params: yearMonthDay)
export interface BusinessManualReserveReservableTimeResponse {
  statusCode: number;
  message?: string;
  data?: {
    calendar?: unknown;
    startTime: string;
    endTime: string;
  };
}

const getBusinessManualReserveReservableTime = async (params: {
  yearMonthDay: string;
}): Promise<BusinessManualReserveReservableTimeResponse> => {
  const { data } = await privateHttp.get<BusinessManualReserveReservableTimeResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.RESERVABLE_TIME,
    { params }
  );
  return data;
};

export const useBusinessManualReserveReservableTime = (
  yearMonthDay: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_MANUAL_RESERVE_QUERY_KEYS.reservableTime,
      yearMonthDay,
    ],
    queryFn: () => getBusinessManualReserveReservableTime({ yearMonthDay }),
    enabled: enabled && !!yearMonthDay,
    select: (res) => res.data ?? null,
    placeholderData: keepPreviousData,
  });
};

// 수동(매뉴얼) 예약 - 계약 조회 (GET /business/manual/reserve/contract, params: petSeq)
export interface BusinessManualReserveContractResponse {
  statusCode: number;
  message?: string;
  data?: {
    companySeq: number;
    userSeq: number;
    petSeq: number;
    contractSeq: number;
    userName: string | null;
    phoneNumber: string | null;
    addr: string | null;
    addrDetail: string | null;
    signatureFileSeq: number;
    fileUrl: string | null;
    petName: string;
    privacyYn: string;
    contractName: string;
    contractUrl: string;
    contractContents: string;
    contractDocJson?: string | null;
    renderedVersionSeq?: number | null;
    renderedPdfSeq?: number | null;
    renderedPdfUrl?: string | null;
    renderedPageFileSeqs?: string | null;
    renderedPageFileUrls?: string[] | null;
    placementManifest?: string | null;
    /** 사장님 활성 기본 도장 이미지 URL */
    sealImageUrl?: string | null;
    /** [varName] 마커 prefilled 값 */
    variables?: Record<string, string> | null;
    companyInfo: {
      companyName: string | null;
      businessNumber: string | null;
    };
    userInfo: {
      userName: string | null;
      nickname: string | null;
      email: string | null;
      phoneNumber: string | null;
      address: string | null;
    };
    petInfo: {
      petName: string;
      petBirthday: string;
      petBreed: string;
      petWeight: string;
      neuter: string;
      inoculation: string;
      personality: string;
      allergy: string;
      memo: string;
    };
  };
}

const getBusinessManualReserveContract = async (params: {
  petSeq: number;
}): Promise<BusinessManualReserveContractResponse> => {
  const { data } = await privateHttp.get<BusinessManualReserveContractResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.CONTRACT,
    { params }
  );
  return data;
};

export const useBusinessManualReserveGetContract = (
  petSeq: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...BUSINESS_MANUAL_RESERVE_QUERY_KEYS.contract, petSeq],
    queryFn: () => getBusinessManualReserveContract({ petSeq }),
    enabled: enabled && !!petSeq && petSeq > 0,
  });
};

// 수동(매뉴얼) 예약 - 계약 생성 (POST /business/manual/reserve/contract, 파라미터/리턴은 user와 동일)
interface CreateContractRequest {
  data: {
    companySeq: number;
    userSeq: number;
    petSeq: number;
    contractSeq: number;
    userName: string;
    phoneNumber: string;
    addr: string | null;
    addrDetail: string | null;
    signatureFileSeq: number | null;
    fileUrl: string | null;
    petName: string;
    privacyYn: string | null;
    contractName: string;
    contractUrl: string | null;
    contractContents?: string;
    contractDocJson?: string | null;
    fieldValues?: Record<string, string>;
    companyInfo?: {
      companyName: string | null;
      businessNumber: string | null;
    };
    userInfo?: {
      userName: string | null;
      nickname: string | null;
      email: string | null;
      phoneNumber: string | null;
      address: string | null;
    };
    petInfo?: {
      petName: string;
      petBirthday: string;
      petBreed: string;
      petWeight: string;
      neuter: string;
      inoculation: string;
      personality: string;
      allergy: string;
      memo: string;
    };
  };
  signature: File | string;
}

interface CreateContractResponse {
  statusCode: number;
  message: string;
  data?: {
    contractSeq: number;
    status: string;
  };
}

const createContractBusinessManual = async (
  request: CreateContractRequest
): Promise<CreateContractResponse> => {
  const formData = new FormData();
  formData.append("param", JSON.stringify(request.data));
  if (request.signature) {
    if (request.signature instanceof File) {
      formData.append("file", request.signature);
    } else if (
      typeof request.signature === "string" &&
      request.signature.startsWith("data:")
    ) {
      const response = await fetch(request.signature);
      const blob = await response.blob();
      formData.append("file", blob, "signature.png");
    }
  }
  const { data } = await privateHttp.post<CreateContractResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.CONTRACT,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return data;
};

export const useCreateContractBusinessManual = () => {
  return useMutation({
    mutationFn: createContractBusinessManual,
  });
};

// 수동(매뉴얼) 예약 - 캘린더 조회 (GET /business/manual/reserve/calendar, params: petSeq, yearMonthDay)
// 응답 구조: { statusCode, data: { [yyyyMMdd]: string[] }, message }
//  - data 자체가 일자별 스케줄 맵이며, CalendarNew의 schedules prop과 동일한 형태
export interface BusinessManualReserveCalendarResponse {
  statusCode: number;
  message?: string;
  data?: { [key: string]: string[] };
}

const getBusinessManualReserveCalendar = async (params: {
  petSeq: number;
  yearMonthDay: string;
}): Promise<BusinessManualReserveCalendarResponse> => {
  const { data } = await privateHttp.get<BusinessManualReserveCalendarResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.CALENDAR,
    { params }
  );
  return data;
};

export const useBusinessManualReserveCalendar = (
  petSeq: number,
  yearMonthDay: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_MANUAL_RESERVE_QUERY_KEYS.calendar,
      petSeq,
      yearMonthDay,
    ],
    queryFn: () => getBusinessManualReserveCalendar({ petSeq, yearMonthDay }),
    enabled: enabled && !!petSeq && petSeq > 0 && !!yearMonthDay,
    select: (res) => res.data ?? {},
  });
};

// 수동(매뉴얼) 예약 생성 (POST /business/manual/reserve, 요청/응답은 /user/reserve와 동일)
// 비지니스 수동 예약은 같은 매장 중복 시 4009 응답으로 차단됨
interface BusinessManualReserveRequest {
  reservationSeq: number | null;
  cdProductType: string;
  gradeConfirm: boolean;
  paymentCoinTotal: number | null;
  // companySeq 는 서버가 토큰에서 결정한다 (BusinessManualReserveServiceImpl#createReservation).
  tickets: Array<{
    purchaseTicketSeq: number;
    productTicketSeq: number;
    paymentTicket: number;
  }>;
  reserveForm: {
    productSeq?: number;
    dates?: string[];
    dtReservStart?: string;
    dtReservEnd?: string;
    startTime?: string;
    endTime?: string;
    dayOfWeeks?: Array<{
      cdDayOfWeek: string;
      dayOfWeekName: string;
      select: boolean;
    }>;
    paymentCoin?: number;
    additionals: Array<{
      productSeq?: number;
      additionalCompanySeq?: number;
      paymentCoin: number;
      additionalName: string;
      active: boolean;
      select: boolean;
    }>;
    pets: Array<{
      petSeq: number;
      petName: string;
      select: boolean;
    }>;
    memo: string | null;
  };
}

interface BusinessManualReserveResponse {
  statusCode: number;
  message: string;
  data?: {
    hasContract?: boolean;
    reservationSeq?: number;
    responseCode?: string;
    conflictCompanySeq?: number;
    conflictCompanyName?: string;
  };
}

export const createBusinessManualReserve = async (
  request: BusinessManualReserveRequest
): Promise<BusinessManualReserveResponse> => {
  const { data } = await privateHttp.post<BusinessManualReserveResponse>(
    API_ENDPOINTS.BUSINESS_MANUAL_RESERVE.RESERVE,
    request
  );
  return data;
};

export const useCreateBusinessManualReserve = () => {
  return useMutation({
    mutationFn: createBusinessManualReserve,
  });
};

// React Query 키
export const BUSINESS_RESERVE_QUERY_KEYS = {
  list: ["business", "reserve", "list"] as const,
  detail: ["business", "reserve", "detail"] as const,
} as const;

// 예약 목록 조회 훅
// 날짜 필터 없이 상태/유형 기준 전체를 조회한다.
export const useBusinessReserveList = (
  cdReservStatus?: string,
  cdReservType?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_RESERVE_QUERY_KEYS.list,
      cdReservStatus,
      cdReservType,
    ],
    queryFn: () =>
      getBusinessReserveList({
        cdReservStatus,
        cdReservType,
      }),
    enabled: enabled && (!!cdReservStatus || !!cdReservType),
  });
};

// 예약 상태 업데이트 API
export const updateBusinessReserve = async (
  request: BusinessReserveUpdateRequest
): Promise<BusinessReserveUpdateResponse> => {
  const { data } = await privateHttp.patch<BusinessReserveUpdateResponse>(
    API_ENDPOINTS.BUSINESS_RESERVE.UPDATE,
    request
  );
  return data;
};

// 예약 상태 업데이트 훅
export const useUpdateBusinessReserve = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessReserve,
    onSuccess: () => {
      // 예약 목록과 상세 정보 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: BUSINESS_RESERVE_QUERY_KEYS.list,
      });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_RESERVE_QUERY_KEYS.detail,
      });
    },
  });
};

// 예약 상세 조회 훅
export const useBusinessReserveDetail = (
  reservationSeq: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...BUSINESS_RESERVE_QUERY_KEYS.detail, reservationSeq],
    queryFn: () => getBusinessReserveDetail({ reservationSeq }),
    enabled: enabled && !!reservationSeq,
  });
};
